import { cn } from "@/util/cn";
import { useLocation } from "react-router";
import { useBuyPanelsStore } from "../store";
import { useCurrentPath } from "@/hooks/useCurrentPath";
import { Fragment } from "react";
import { getBuyPanelsRootSlug } from "../route";

export default function Stepper({ className }: { className?: string }) {
  const currentPath = useCurrentPath();
  const steps = useBuyPanelsStore((state) => state.steps);
  const location = useLocation();
  const [base, slug] = location.pathname.split("/").filter(Boolean);
  const rootSlug = getBuyPanelsRootSlug(location.pathname);

  if (currentPath === "confirmation") {
    return null;
  }

  // Filter out confirmation step and map to display steps
  const visibleSteps = steps
    .filter((step) => step.slug !== "confirmation")
    .map((step, index) => ({
      number: index + 1,
      label: step.title,
      slug: step.slug,
      isCompleted: step.isCompleted,
    }));

  return (
    <div className={cn("w-full max-w-7xl mx-auto px-2 py-4 sm:px-4 sm:py-6 md:py-8", className)}>
      <div className="flex items-start justify-center gap-1 sm:gap-2 md:gap-4">
        {visibleSteps.map((step, index) => {
          // Check if we're on the first step (index route)
          const isOnIndexRoute = !slug && base === rootSlug;
          const isFirstStep = index === 0;

          // Determine if this step is active
          const isActive = slug
            ? slug === step.slug
            : (isOnIndexRoute && isFirstStep) || base === step.slug;

          const isCompleted = step.isCompleted;
          const isVisited = isCompleted && !isActive;

          return (
            <Fragment key={step.slug}>
              <div className="flex flex-col items-center gap-1 sm:gap-2 min-w-0">
                <div
                  className={cn(
                    "w-7 h-7 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center border-2 transition-all bg-transparent shrink-0",
                    isActive
                      ? "border-brand-orange-500 text-brand-orange-500"
                      : isVisited
                        ? "border-brand-blue-300 text-brand-blue-300"
                        : "border-brand-neutral-500 text-brand-neutral-500"
                  )}
                >
                  <span className="text-xs sm:text-base md:text-lg font-semibold">{step.number}</span>
                </div>
                <span
                  className={cn(
                    "text-[10px] sm:text-xs md:text-sm font-medium text-center leading-tight max-w-[50px] sm:max-w-[80px] md:max-w-none  sm:whitespace-normal",
                    isActive
                      ? "text-white"
                      : isVisited
                        ? "text-gray-300"
                        : "text-brand-neutral-500"
                  )}
                >
                  {step.label}
                </span>
              </div>

              {/* Connecting Line - Aligned with circle center */}
              {index < visibleSteps.length - 1 && (
                <div className="flex items-center h-7 sm:h-10 md:h-12">
                  <div
                    className={cn(
                      "h-0.5 w-6 sm:w-16 md:w-24 lg:w-32 xl:w-40 mx-0.5 sm:mx-1 md:mx-2 transition-all shrink-0",
                      isCompleted
                        ? "bg-brand-blue-300"
                        : "bg-brand-neutral-500"
                    )}
                  />
                </div>
              )}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}
